import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper/index';
import CustomiseOffer from '../flexiApprovedView/CustomiseOffer';
import styles from '../flexiApprovedView/FlexiApprovedView.module.css';
import { GetFormalOffersResponse } from '@iwoca/lapi-client/edge';
import ApprovalHeader from '../approvalHeader/ApprovalHeader';
import { TitleSectionWrapper } from '../../../pageStructure/titleSectionWrapper/TitleSectionWrapper';
import { FormalOffer } from '../../MyOffers.types';
import InlineEarlyRepaymentCalculator from '../inlineEarlyRepaymentCalculator/InlineEarlyRepaymentCalculator';
import { LoanSummary } from '../flexiApprovedView/loanSummary/LoanSummary';
import { Button } from '@iwoca/orion';
import { FormattedMessage, useIntl } from 'react-intl';
import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals';
import messages from '../flexiApprovedView/CustomiseOffer.messages';
import { Dispatch } from 'redux';
import { buildLocationWithSearch } from '../../../drawDown/DrawDownHelper';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { connect } from 'react-redux';
import EarlyRepaymentCalculator from '../earlyRepaymentCalculator/EarlyRepayment';
import IwMedia from '../../../../iwMedia';
import { TrustPilot } from '../../../TrustPilot/TrustPilot';
import { FooterSectionWrapper } from '../../../pageStructure/footerSectionWrapper/FooterSectionwraper';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { XSellTitle } from '../xsell/xsellTitle/xsellTitle';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    startDrawdown: ({
        amount,
        offerId
    }: {
        amount: number;
        offerId: FormalOffer['offer_id'];
    }) => {
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/funding-requirements/',
                offerId,
                amount
            })
        });
    }
});

type TNewApprovedViewProps = ReturnType<typeof mapDispatchToProps> & {
    maxDate: string;
    loanDuration: number;
    drawdownAmount: number;
    setDrawdownAmount: (drawdownAmount: number) => void;
    selectedOffer: FormalOffer | null;
    updateSelectedOffer: (offer: FormalOffer) => void;
    formalOffers: NonNullable<GetFormalOffersResponse['data']>;
    timeoutDate: string;
    maxCashAmount: number;
    showNewUI: boolean;
};

const NewApprovedView = ({
    maxDate,
    loanDuration,
    drawdownAmount,
    setDrawdownAmount,
    selectedOffer,
    updateSelectedOffer,
    formalOffers,
    timeoutDate,
    maxCashAmount,
    showNewUI,
    startDrawdown
}: TNewApprovedViewProps) => {
    const intl = useIntl();
    const amountInRange = selectedOffer?.available_cash! >= drawdownAmount;

    return (
        <section>
            {getCountry() === 'DE' ? (
                <XSellTitle
                    expiryDate={timeoutDate}
                    maxCashAmount={maxCashAmount}
                />
            ) : (
                <TitleSectionWrapper>
                    <ApprovalHeader
                        approvalStatusTimeOut={timeoutDate}
                        maxCashAmount={maxCashAmount}
                    />
                </TitleSectionWrapper>
            )}
            <MainSectionWrapper
                className={styles.mainSection}
                innerWrapperClassName={styles.mainSectionRow}
            >
                <div
                    className={styles.contentColumn}
                    data-testId="new-approval-view-left-column"
                >
                    <CustomiseOffer
                        drawdownAmount={drawdownAmount}
                        setDrawdownAmount={setDrawdownAmount}
                        selectedOffer={selectedOffer}
                        updateSelectedOffer={updateSelectedOffer}
                        formalOffers={formalOffers}
                        showNewUI={showNewUI}
                    />

                    {selectedOffer && (
                        <IwMedia from="medium">
                            <InlineEarlyRepaymentCalculator
                                drawdownAmount={drawdownAmount}
                                offerId={selectedOffer?.offer_id!}
                                productType={selectedOffer?.product_type!}
                                loanDuration={loanDuration}
                                maxDate={maxDate}
                            />
                        </IwMedia>
                    )}
                </div>

                <div
                    className={styles.contentColumn}
                    data-testId="new-approval-view-right-column"
                >
                    <LoanSummary
                        drawdownAmount={drawdownAmount}
                        selectedOffer={selectedOffer}
                        showNewUI={showNewUI}
                    />

                    <div className={styles.ctaContainer}>
                        <Button
                            variant="success"
                            data-testid="start-drawdown"
                            data-action-name="Approval continue button"
                            disabled={Boolean(!selectedOffer) || !amountInRange}
                            onClick={() =>
                                startDrawdown({
                                    amount: drawdownAmount,
                                    offerId: selectedOffer!.offer_id
                                })
                            }
                            className={styles.cta}
                        >
                            <FormattedMessage
                                {...messages.cta}
                                values={{
                                    amount: (
                                        <MoneyNoDecimals
                                            className={styles.summaryAmount}
                                            value={drawdownAmount}
                                        />
                                    )
                                }}
                            />
                        </Button>

                        {selectedOffer &&
                            (amountInRange ? (
                                <div className={styles.ctaHeplerText}>
                                    <FormattedMessage {...messages.CTAinfo} />{' '}
                                </div>
                            ) : (
                                <div className="mt-s text-s font-thin text-error">
                                    <FormattedMessage
                                        {...messages.CTAdisabled}
                                        values={{
                                            availableCash: intlFormatCurrency({
                                                intl,
                                                value: selectedOffer.available_cash!
                                            })
                                        }}
                                    />
                                </div>
                            ))}
                    </div>
                </div>

                {/* currently using the old calculator layout for mobile */}
                {selectedOffer && (
                    <IwMedia to="medium">
                        <div
                            className={styles.contentColumn_transparent}
                            data-testid="mobile-calculator"
                        >
                            <EarlyRepaymentCalculator
                                drawdownAmount={drawdownAmount}
                                offerId={selectedOffer.offer_id!}
                                loanDuration={loanDuration}
                                productType={selectedOffer.product_type!}
                                maxDate={maxDate}
                            />
                        </div>
                    </IwMedia>
                )}
            </MainSectionWrapper>
            <FooterSectionWrapper>
                {getCountry() === 'UK' && <TrustPilot />}
            </FooterSectionWrapper>
        </section>
    );
};

export const NewApprovedViewUI = connect(
    null,
    mapDispatchToProps
)(NewApprovedView);
